import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    alpha,
    useTheme, 
} from '@mui/material';
import {
    PeopleOutline as PeopleIcon, 
    WorkOutline as WorkIcon, 
} from '@mui/icons-material';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmployeeAttendance from './EmployeeAttendance';
import StudentAttendance from './StudentAttendence'; 

const AttendanceComponent = () => {
    const theme = useTheme();
    const [view, setView] = useState('students');
    const location = useLocation();
    const iSchoolId = localStorage.getItem('schoolId');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const nextView = (params.get('view') || '').toLowerCase();
        if (nextView === 'students' || nextView === 'employees') {
            setView(nextView);
        }
    }, [location.search]);

    const handleChange = (event, newView) => {
        if (newView !== null) {
            setView(newView);
        }
    };

    if (!iSchoolId) {
        return (
            // Centered Error Alert
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 100px)' }}>
                <Alert severity="error" sx={{ width: '80%', maxWidth: '500px' }}>
                    School ID not found. Please log in again or contact support.
                </Alert>
            </Box>
        );
    }

    return (
        <PageContainer sx={{ width: '100%' }}>
            <AdminPageHero
                title="Attendance Management"
                subtitle="Review student and employee attendance from one shared workspace."
                metrics={[
                    { label: 'View', value: view === 'students' ? 'Students' : 'Employees' },
                ]}
            />

            <AdminPanelCard
                sx={{ mt: 1.5, borderRadius: 6 }}
                contentSx={{ p: { xs: 1.2, md: 1.4 } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', mb: 2 }}>
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        onChange={handleChange}
                        aria-label="attendance view toggle"
                        color="primary"
                        sx={{
                            bgcolor: '#f5f7fb',
                            borderRadius: 6,
                            p: 0.45,
                            overflow: 'hidden',
                            '& .MuiToggleButtonGroup-grouped': {
                                px: { xs: 2, sm: 2.6 },
                                py: 0.95,
                                fontSize: { xs: '0.83rem', sm: '0.88rem' },
                                textTransform: 'none',
                                fontWeight: 700,
                                border: 'none',
                                borderRadius: '6px !important',
                                color: theme.palette.text.secondary,
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                    bgcolor: '#ffffff',
                                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                                    '&:hover': {
                                        bgcolor: '#ffffff',
                                    },
                                },
                                '&:not(.Mui-selected):hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                },
                            },
                        }}
                    >
                        <ToggleButton value="students" aria-label="student attendance">
                            <PeopleIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                            Students
                        </ToggleButton>
                        <ToggleButton value="employees" aria-label="employee attendance">
                            <WorkIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                            Employees
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        minHeight: 400,
                    }}
                >
                    {view === 'students' ? (
                        <StudentAttendance iSchoolId={iSchoolId} />
                    ) : (
                        <EmployeeAttendance iSchoolId={iSchoolId} />
                    )}
                </Box>
            </AdminPanelCard>
        </PageContainer>
    );
};

export default AttendanceComponent;
